import { useState, useEffect, useRef } from "react";
import useSwipeBack from "./useSwipeBack";
import { AudioAltar } from "./AudioAltar";

// Recitations by Egzon Ibrahimi — a curated collection of full-surah recordings
// from the young Kosovar qari whose voice has moved listeners around the world.
//
// Audio is hosted by Quran Central: https://podcasts.qurancentral.com/egzon-ibrahimi/NNN.mp3
// We play full-surah MP3s (not ayah-by-ayah) through a single HTMLAudioElement
// with media session API for lock-screen controls. No ayah tracking, no speed
// control — just the raw beauty of his voice, clean and simple.
//
// This tool is dedicated to Khudaija Jamal.

const SURAHS = [
  { num: 2,   name: "Al-Baqarah",   arabic: "البقرة",     length: "2:09:39" },
  { num: 18,  name: "Al-Kahf",      arabic: "الكهف",      length: "31:45" },
  { num: 33,  name: "Ayatul Light", arabic: "آية النور",  length: "1:37",   note: "Surah Al-Ahzab, Ayah 35" },
  { num: 36,  name: "Ya-Seen",      arabic: "يس",         length: "17:45" },
  { num: 50,  name: "Qaaf",         arabic: "ق",          length: "9:19" },
  { num: 51,  name: "Adh-Dhariyat", arabic: "الذاريات",   length: "8:09" },
  { num: 55,  name: "Ar-Rahman",    arabic: "الرحمن",     length: "11:01" },
  { num: 56,  name: "Al-Waqia",     arabic: "الواقعة",    length: "11:50" },
  { num: 61,  name: "As-Saff",      arabic: "الصف",       length: "5:20" },
  { num: 62,  name: "Al-Jumua",     arabic: "الجمعة",     length: "4:00" },
  { num: 66,  name: "At-Tahrim",    arabic: "التحريم",    length: "6:49" },
  { num: 67,  name: "Al-Mulk",      arabic: "الملك",      length: "8:21" },
  { num: 75,  name: "Al-Qiyama",    arabic: "القيامة",    length: "4:23" },
  { num: 76,  name: "Al-Insan",     arabic: "الإنسان",    length: "7:08" },
  { num: 78,  name: "An-Naba",      arabic: "النبأ",      length: "4:14" },
  { num: 87,  name: "Al-A'la",      arabic: "الأعلى",     length: "1:41" },
  { num: 89,  name: "Al-Fajr",      arabic: "الفجر",      length: "3:48" },
  { num: 91,  name: "Ash-Shams",    arabic: "الشمس",      length: "1:20" },
  { num: 95,  name: "At-Tin",       arabic: "التين",       length: "0:46" },
  { num: 102, name: "At-Takathur",  arabic: "التكاثر",    length: "1:32" },
  { num: 104, name: "Al-Humaza",    arabic: "الهمزة",     length: "1:52" },
  { num: 106, name: "Quraish",      arabic: "قريش",       length: "1:16" },
  { num: 108, name: "Al-Kauthar",   arabic: "الكوثر",     length: "1:02" },
  { num: 110, name: "An-Nasr",      arabic: "النصر",      length: "1:33" },
  { num: 112, name: "Al-Ikhlas",    arabic: "الإخلاص",    length: "1:28" },
];

// Cloudflare blocks direct hotlinking from our origin, so we proxy through the
// Sirat backend which makes a clean server-to-server fetch with the right
// Referer/User-Agent headers and streams the response back.
const BACKEND = "https://azaan-backend.onrender.com";
const audioUrl = (num) => `${BACKEND}/api/audio/egzon/${String(num).padStart(3, "0")}`;

// Module-level singleton — survives component unmount so audio keeps playing
// when the user switches tabs. Never reassigned once created.
let _audio = null;
let _playingNum = null;
const _listeners = new Set();
const _notify = () => _listeners.forEach(cb => { try { cb(); } catch {} });
const getAudio = () => {
  if (!_audio) {
    _audio = new Audio();
    _audio.preload = "auto";
    _audio.addEventListener("play", _notify);
    _audio.addEventListener("pause", _notify);
    _audio.addEventListener("ended", _notify);
  }
  return _audio;
};

// Public API used by the global mini player in App.js.
export const egzonPlayer = {
  SURAHS,
  get playingNum() { return _playingNum; },
  get isPlaying() { return !!_audio && !_audio.paused && _playingNum != null; },
  subscribe(cb) { _listeners.add(cb); return () => _listeners.delete(cb); },
  pause() { if (_audio) _audio.pause(); },
  resume() { if (_audio) _audio.play().catch(() => {}); },
  stop() {
    if (_audio) { _audio.pause(); try { _audio.currentTime = 0; } catch {} }
    _playingNum = null;
    if ("mediaSession" in navigator) { try { navigator.mediaSession.playbackState = "none"; } catch {} }
    _notify();
  },
};

const fmtTime = (sec) => {
  if (!isFinite(sec) || sec < 0) return "0:00";
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  if (m >= 60) {
    const h = Math.floor(m / 60);
    const mm = m % 60;
    return `${h}:${String(mm).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  }
  return `${m}:${String(s).padStart(2, "0")}`;
};

export default function EgzonRecitations({ darkMode, onBack }) {
  const swipe = useSwipeBack(onBack);
  const audioRef = useRef(null);
  const handlersRef = useRef({});
  const [playingNum, setPlayingNum] = useState(() => _playingNum);
  const [isPlaying, setIsPlaying] = useState(() => _audio ? !_audio.paused : false);
  const [loading, setLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(() => _audio ? _audio.currentTime : 0);
  const [duration, setDuration] = useState(() => (_audio && isFinite(_audio.duration)) ? _audio.duration : 0);
  const [errorMsg, setErrorMsg] = useState(null);

  // Attach/detach event listeners on mount/unmount — never pause the audio
  useEffect(() => {
    const a = getAudio();
    audioRef.current = a;

    const onMeta     = () => handlersRef.current.onMeta();
    const onTime     = () => handlersRef.current.onTime();
    const onPlay     = () => handlersRef.current.onPlay();
    const onPause    = () => handlersRef.current.onPause();
    const onEnded    = () => handlersRef.current.onEnded();
    const onError    = () => handlersRef.current.onError();

    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("timeupdate",     onTime);
    a.addEventListener("play",           onPlay);
    a.addEventListener("pause",          onPause);
    a.addEventListener("ended",          onEnded);
    a.addEventListener("error",          onError);

    if ("mediaSession" in navigator) {
      try {
        navigator.mediaSession.setActionHandler("play",         () => a.play().catch(() => {}));
        navigator.mediaSession.setActionHandler("pause",        () => a.pause());
        navigator.mediaSession.setActionHandler("seekbackward", (d) => { a.currentTime = Math.max(0, a.currentTime - (d.seekOffset || 10)); });
        navigator.mediaSession.setActionHandler("seekforward",  (d) => { a.currentTime = Math.min(a.duration || 0, a.currentTime + (d.seekOffset || 10)); });
      } catch {}
    }

    return () => {
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("timeupdate",     onTime);
      a.removeEventListener("play",           onPlay);
      a.removeEventListener("pause",          onPause);
      a.removeEventListener("ended",          onEnded);
      a.removeEventListener("error",          onError);
      // Do NOT pause — audio keeps playing when user switches tabs
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  handlersRef.current = {
    onMeta: () => {
      const a = audioRef.current;
      if (!a) return;
      setDuration(a.duration);
      setLoading(false);
    },
    onTime: () => {
      const a = audioRef.current;
      if (!a) return;
      setCurrentTime(a.currentTime);
      if ("mediaSession" in navigator && a.duration && isFinite(a.duration)) {
        try {
          navigator.mediaSession.setPositionState({
            duration: a.duration,
            playbackRate: a.playbackRate,
            position: a.currentTime,
          });
        } catch {}
      }
    },
    onPlay:  () => setIsPlaying(true),
    onPause: () => setIsPlaying(false),
    onEnded: () => {
      _playingNum = null;
      _notify();
      setIsPlaying(false);
      setPlayingNum(null);
      if ("mediaSession" in navigator) {
        try { navigator.mediaSession.playbackState = "none"; } catch {}
      }
    },
    onError: () => {
      const a = audioRef.current;
      const code = a && a.error ? a.error.code : "?";
      const codeMap = { 1: "ABORTED", 2: "NETWORK", 3: "DECODE", 4: "SRC_NOT_SUPPORTED" };
      const label = codeMap[code] || `code ${code}`;
      // eslint-disable-next-line no-console
      console.error("[Egzon] audio error:", label, a && a.src);
      setErrorMsg(`Audio error: ${label} — check the URL works in Safari: ${a && a.src ? a.src : ""}`);
      setLoading(false);
      setIsPlaying(false);
    },
  };

  const playSurah = (surah) => {
    const a = audioRef.current;
    if (!a) { setErrorMsg("Audio not ready — please retry"); return; }
    setErrorMsg(null);
    _playingNum = surah.num;
    _notify();
    setPlayingNum(surah.num);
    setLoading(true);
    setCurrentTime(0);
    setDuration(0);

    if ("mediaSession" in navigator) {
      try {
        navigator.mediaSession.metadata = new window.MediaMetadata({
          title: `${surah.name}${surah.note ? " · " + surah.note : ""}`,
          artist: "Egzon Ibrahimi",
          album: "Sirat · Featured Recitation",
          artwork: [
            { src: "/logo192.png", sizes: "192x192", type: "image/png" },
            { src: "/logo512.png", sizes: "512x512", type: "image/png" },
          ],
        });
        navigator.mediaSession.playbackState = "playing";
      } catch {}
    }

    a.src = audioUrl(surah.num);
    a.load();
    const p = a.play();
    if (p && typeof p.catch === "function") {
      p.catch(err => {
        // eslint-disable-next-line no-console
        console.error("[Egzon] play() rejected:", err);
        setErrorMsg(`Play failed: ${err && err.name ? err.name : "unknown"} · ${err && err.message ? err.message : ""}`);
        setLoading(false);
        setIsPlaying(false);
      });
    }
  };

  const togglePlay = (surah) => {
    const a = audioRef.current;
    if (!a) return;
    if (playingNum === surah.num) {
      if (isPlaying) a.pause();
      else a.play().catch(err => {
        // eslint-disable-next-line no-console
        console.error("[Egzon] resume play() rejected:", err);
        setErrorMsg(`Resume failed: ${err && err.message ? err.message : "unknown"}`);
      });
    } else {
      playSurah(surah);
    }
  };

  const seek = (newTime) => {
    if (audioRef.current && isFinite(newTime)) {
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const gold = darkMode ? "#ffb300" : "#b87008";
  const goldFaint = darkMode ? "rgba(255,179,0,0.08)" : "rgba(184,112,8,0.06)";
  const goldBorder = darkMode ? "rgba(255,179,0,0.25)" : "rgba(184,112,8,0.25)";

  return (
    <div
      onTouchStart={swipe.onTouchStart}
      onTouchMove={swipe.onTouchMove}
      onTouchEnd={swipe.onTouchEnd}
      style={{ display: "flex", flexDirection: "column", height: "100%", background: "var(--bg)", overflowY: "auto", paddingBottom: playingNum !== null ? "calc(160px + env(safe-area-inset-bottom))" : "calc(80px + env(safe-area-inset-bottom))" }}
    >
      {/* Header */}
      <div style={{ padding: "16px 16px 14px", display: "flex", alignItems: "center", gap: "12px", borderBottom: `1px solid var(--border)` }}>
        <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--text)", fontSize: "24px", cursor: "pointer", padding: "4px", lineHeight: 1 }}>‹</button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: "17px", fontWeight: "800", color: "var(--text)" }}>Recitations by Egzon Ibrahimi</div>
          <div style={{ fontSize: "11px", color: gold, fontWeight: "600", marginTop: "2px", fontStyle: "italic" }}>For Khudaija Jamal</div>
        </div>
      </div>

      {/* Error banner */}
      {errorMsg && (
        <div style={{ margin: "12px 16px 0", padding: "10px 14px", background: "rgba(200,60,60,0.08)", border: "1px solid rgba(200,60,60,0.35)", borderRadius: "12px", fontSize: "11px", color: "#e87878", lineHeight: 1.5, wordBreak: "break-all" }}>
          ⚠ {errorMsg}
        </div>
      )}

      {/* Hero — AudioAltar */}
      <AudioAltar
        eyebrow="RECITATIONS FROM · QARI #25"
        title="Egzon Ibrahimi"
        arabicName="إيغزون إبراهيمي"
        tone="gold"
        facts={[
          { label: "ORIGIN", value: "Kosovo" },
          { label: "SINCE", value: "2015" },
          { label: "STYLE", value: "Murattal" },
          { label: "RECITATIONS", value: String(SURAHS.length) },
        ]}
        bio="A young Kosovar qari whose Surah Yaseen and Ar-Rahman recitations have moved hearts worldwide. His voice carries a clarity and sincerity that speaks directly to the soul."
        quote={{ text: "The reciter adorns the Quran, but the Quran is the greatest adornment.", attribution: "Classical maxim" }}
        primaryCta={{ label: "▶ Play Al-Baqarah", onPlay: () => playSurah(SURAHS[0]) }}
      />

      {/* Surah list */}
      <div style={{ padding: "14px 16px 0" }}>
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {SURAHS.map(s => {
            const active = playingNum === s.num;
            return (
              <div
                key={s.num}
                onClick={() => togglePlay(s)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "12px",
                  padding: "12px 14px",
                  borderRadius: "14px",
                  background: active ? goldFaint : "var(--s1)",
                  border: `1px solid ${active ? goldBorder : "var(--border)"}`,
                  cursor: "pointer",
                  transition: "all 0.2s ease",
                }}
              >
                <div style={{
                  width: "40px", height: "40px", borderRadius: "12px",
                  background: active ? gold : "var(--s2)",
                  border: `1px solid ${active ? gold : "var(--border)"}`,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: active ? "#000" : "var(--text)",
                  fontSize: active && loading ? "12px" : "14px",
                  fontWeight: "800",
                  flexShrink: 0,
                }}>
                  {active && loading ? <span style={{display:"inline-block",width:"14px",height:"14px",border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#000",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/> : active && isPlaying ? "⏸" : "▶"}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                    <div style={{ fontSize: "14px", fontWeight: "700", color: "var(--text)" }}>{s.name}</div>
                    <div style={{ fontSize: "14px", color: gold, fontFamily: "'Scheherazade New', serif" }}>{s.arabic}</div>
                  </div>
                  <div style={{ fontSize: "10px", color: "var(--muted)", marginTop: "2px" }}>
                    {active && loading ? <span style={{color:gold}}>Buffering...</span> : <>{s.note || `Surah ${s.num}`} · {s.length}</>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Credit */}
        <div style={{
          marginTop: "20px",
          padding: "12px 14px",
          background: "var(--s1)",
          border: "1px solid var(--border)",
          borderRadius: "12px",
          fontSize: "10px",
          color: "var(--muted)",
          lineHeight: 1.6,
          textAlign: "center",
        }}>
          Audio streamed from <span style={{ color: gold, fontWeight: "700" }}>qurancentral.com</span> · All rights belong to the reciter
        </div>
      </div>

      {/* Fixed mini player when a surah is playing */}
      {playingNum !== null && (() => {
        const current = SURAHS.find(s => s.num === playingNum);
        if (!current) return null;
        const pct = duration > 0 ? (currentTime / duration) * 100 : 0;
        return (
          <div
            onClick={e => e.stopPropagation()}
            onTouchStart={e => e.stopPropagation()}
            style={{
              position: "fixed",
              left: 0, right: 0,
              bottom: "calc(68px + env(safe-area-inset-bottom))",
              maxWidth: "480px",
              margin: "0 auto",
              padding: "10px 14px",
              background: darkMode ? "rgba(10,14,20,0.96)" : "rgba(255,253,247,0.96)",
              backdropFilter: "blur(12px)",
              WebkitBackdropFilter: "blur(12px)",
              borderTop: `1px solid ${goldBorder}`,
              zIndex: 90,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <button
                onClick={() => togglePlay(current)}
                style={{
                  width: "38px", height: "38px", borderRadius: "12px",
                  background: gold, border: "none",
                  color: "#000", fontSize: "14px", fontWeight: "800",
                  cursor: "pointer", flexShrink: 0,
                }}
              >
                {loading ? <span style={{display:"inline-block",width:"14px",height:"14px",border:"2px solid rgba(0,0,0,0.2)",borderTopColor:"#000",borderRadius:"50%",animation:"spin 0.7s linear infinite"}}/> : isPlaying ? "⏸" : "▶"}
              </button>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: "12px", fontWeight: "700", color: "var(--text)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {current.name} · Egzon Ibrahimi
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginTop: "4px" }}>
                  <span style={{ fontSize: "9px", color: "var(--muted)", fontVariantNumeric: "tabular-nums", minWidth: "32px" }}>{fmtTime(currentTime)}</span>
                  <input
                    type="range"
                    min="0"
                    max={duration || 0}
                    value={currentTime}
                    onChange={e => seek(parseFloat(e.target.value))}
                    onTouchStart={e => e.stopPropagation()}
                    onTouchMove={e => e.stopPropagation()}
                    style={{ flex: 1, height: "3px", accentColor: gold }}
                  />
                  <span style={{ fontSize: "9px", color: "var(--muted)", fontVariantNumeric: "tabular-nums", minWidth: "32px", textAlign: "right" }}>{fmtTime(duration)}</span>
                </div>
                <div style={{ height: "2px", background: "var(--border)", borderRadius: "1px", overflow: "hidden", marginTop: "2px" }}>
                  <div style={{ height: "100%", width: `${pct}%`, background: gold, transition: "width 0.2s ease" }}/>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}
